import { useState, useEffect } from 'react';
import { useI18n } from './i18n';
import { motion } from 'framer-motion';
import { Send, Clock, Monitor, ShieldCheck, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const trustIcons = [Clock, Monitor, ShieldCheck];

// Human-readable labels for quiz question IDs
const questionLabels = {
  savoirFaire: 'Savoir-faire technique',
  support: 'Support pédagogique',
  competences: 'Compétences ciblées',
  participants: 'Nombre de participants',
  duree: 'Durée souhaitée',
  theorie: 'Apport théorique',
  lieu: 'Lieu de formation',
};

export default function LandingContact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [quizData, setQuizData] = useState(null);

  // Load quiz data from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('anticiper_quiz');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.answers && Object.keys(parsed.answers).length > 0) {
          setQuizData(parsed);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  const dismissQuiz = () => {
    setQuizData(null);
    localStorage.removeItem('anticiper_quiz');
  };

  // Build a text summary of quiz answers for the message
  const buildQuizSummary = () => {
    if (!quizData) return '';
    const lines = ['--- Résultats du configurateur ---'];
    for (const [key, value] of Object.entries(quizData.answers)) {
      const label = questionLabels[key] || key;
      const display = Array.isArray(value) ? value.join(', ') : value;
      if (display) lines.push(`${label} : ${display}`);
    }
    if (quizData.recommendations && quizData.recommendations.length > 0) {
      lines.push(`Recommandations : ${quizData.recommendations.join(', ')}`);
    }
    return lines.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // Send to backend
    try {
      await axios.post(`https://anticiper.app/api/contact`, {
        name: form.name,
        email: form.email,
        company: form.company,
        phone: form.phone,
        message: form.message,
        quiz_data: quizData || null,
      });
      toast.success(t.contact.form.success);
      setForm({ name: '', email: '', company: '', phone: '', message: '' });
      localStorage.removeItem('anticiper_quiz');
      setQuizData(null);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="pt-32 pb-24 bg-gradient-to-b from-slate-50 to-white min-h-screen">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Info */}
          <motion.div initial="hidden" animate="visible">
            <motion.span
              variants={fadeUp}
              custom={0}
              className="inline-block text-xs font-semibold tracking-[0.2em] text-[#34B2C8] mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {t.contact.overline}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-4xl font-light text-slate-900 mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t.contact.title}
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base text-slate-500 leading-relaxed mb-10">
              {t.contact.subtitle}
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="space-y-4">
              {t.contact.trust.map((item, i) => {
                const Icon = trustIcons[i];
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#34B2C8]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} className="text-[#34B2C8]" />
                    </div>
                    <span className="text-sm text-slate-600">{item}</span>
                  </div>
                );
              })}
            </motion.div>

            {/* Quiz Results Summary */}
            {quizData && (
              <motion.div
                variants={fadeUp}
                custom={4}
                className="mt-10 p-5 bg-[#34B2C8]/5 rounded-xl border border-[#34B2C8]/15"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-[#34B2C8]" />
                    <h4 className="text-sm font-semibold text-slate-900">Votre configuration</h4>
                  </div>
                  <button
                    onClick={dismissQuiz}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                    title="Supprimer"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="space-y-2.5">
                  {Object.entries(quizData.answers).map(([key, value]) => {
                    const display = Array.isArray(value) ? value.join(', ') : value;
                    if (!display) return null;
                    return (
                      <div key={key} className="flex gap-2">
                        <span className="text-xs font-medium text-[#34B2C8] min-w-[120px] flex-shrink-0">
                          {questionLabels[key] || key}
                        </span>
                        <span className="text-xs text-slate-600">{display}</span>
                      </div>
                    );
                  })}
                  {quizData.recommendations && quizData.recommendations.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-[#34B2C8]/10 flex gap-2">
                      <span className="text-xs font-medium text-[#34B2C8] min-w-[120px] flex-shrink-0">Recommandations</span>
                      <span className="text-xs font-semibold text-slate-900">
                        {quizData.recommendations.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Ces informations seront jointes à votre demande.
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm"
            >
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t.contact.form.name}</label>
                  <input
                    type="text"
                    required
                    data-testid="contact-name"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34B2C8]/30 focus:border-[#34B2C8] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t.contact.form.email}</label>
                  <input
                    type="email"
                    required
                    data-testid="contact-email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34B2C8]/30 focus:border-[#34B2C8] transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t.contact.form.company}</label>
                    <input
                      type="text"
                      required
                      data-testid="contact-company"
                      value={form.company}
                      onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34B2C8]/30 focus:border-[#34B2C8] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t.contact.form.phone}</label>
                    <input
                      type="tel"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34B2C8]/30 focus:border-[#34B2C8] transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t.contact.form.message}</label>
                  <textarea
                    rows={4}
                    required
                    data-testid="contact-message"
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder={t.contact.form.messagePlaceholder}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34B2C8]/30 focus:border-[#34B2C8] transition-colors resize-none"
                  />
                </div>
              </div>

              {/* Quiz attached indicator */}
              {quizData && (
                <div className="mt-4 flex items-center gap-2 px-3 py-2 bg-[#34B2C8]/5 rounded-lg border border-[#34B2C8]/10">
                  <FileText size={14} className="text-[#34B2C8] flex-shrink-0" />
                  <span className="text-xs text-[#34B2C8] font-medium">Configuration de session jointe à votre message</span>
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] disabled:opacity-60 text-white font-medium py-3 rounded-full transition-all duration-200 hover:scale-[1.01] shadow-lg shadow-[#34B2C8]/20"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={16} />
                    {t.contact.form.submit}
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
